import { isSameArrays } from "../helper/OGcomplianceValidateExcel";

import { DUPLICATE_DATA, EMPTY_VALUE, EMPTY_VALUE_1, EMPTY_VALUE_2, EXCEL_EMPTY_ERR_MSG, EXCEL_Template, HEADER_ERR, HEADER_ERR_MESSAGE, INVALID_CHAR_1, INVALID_CHAR_2 } from "../components/OutletGeoCompliance/OGComplianceBulkUpload/BulkUpdateModal.constant";
import { splitCSVRow, validateField, validateFileMeta } from "./FileHelper";
import { INVALID_DATA_ERROR } from "../components/MerchandisingSets/MerchandizingSetUpload/MSUploadModal.constant";

export const getEmptyValues = ({ row, setFileError, resolve }) => {
  if (!row["Customer code"] || !row["Journey plan code"] || !row["Journey plan description"] ||
     !row["Sequence"] || !row["Week"] || !row["Visit day"]) {
    let error = `${EMPTY_VALUE_1} ${row.__rowNum__ + 1}${EMPTY_VALUE_2}`;
    setFileError({ header: EMPTY_VALUE, message: error });
    resolve("invalid");
    return true;
  }
  return false;
};

export const validateSpecialChar = ({ row, setFileError, resolve, selectedCountryCode, excludeCols=["Week","Sequence","Visit day", "Effective From", "Active/Inactive status","Question","Section","Options"]}) => {
  const normalizedCountryCode = String(selectedCountryCode || "").toLowerCase();
  const mandatoryEntries = Object.keys(row)
    .filter((key) => !excludeCols.includes(key))
    .map((key) => ({ key, value: row[key] }));

  const defaultRegex = /^[A-Za-z0-9](.*[A-Za-z0-9])?$/;
  const invalidValues = mandatoryEntries.filter(({ key, value }) => {
    const normalizedValue = String(value)
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .normalize("NFC");
    const trimmedValue = normalizedValue.trim();

    if (normalizedCountryCode === "vn" && key === "Journey plan description") {
      return normalizedValue !== trimmedValue || !trimmedValue;
    }

    return normalizedValue !== trimmedValue || !defaultRegex.test(trimmedValue);
  });

  if (invalidValues.length) {
    let error = `${INVALID_CHAR_1} ${row.__rowNum__ + 1}${INVALID_CHAR_2}`;
    setFileError({ header: INVALID_DATA_ERROR, message: error });
    resolve("invalid");
    return true;
  }
  return false;
}
export const ValidateOtherFields = ({ filtered, setFileError, resolve, selectedCountryCode }) => {
  if (selectedCountryCode === "id") {
    return false;
  }

  for (const row of filtered) {
    const invalidWeek = validateField({
      row,
      field: "Week",
      setFileError,
      resolve,
      validateFn: (v) => /^[1-6]$/.test(v),
      errorHeader: "Invalid Week",
      errorMessageFn: (r) =>
        `Week in row ${r.__rowNum__ + 1} is invalid${INVALID_CHAR_2}`
    });

    const invalidJpCode = validateField({
      row,
      field: "Journey plan code",
      setFileError,
      resolve,
      validateFn: (v) => selectedCountryCode === "id" ?  v && /^JU?\d+$/.test(v): v && /^J\d+$/.test(v),
      errorHeader: "Invalid Journey plan",
      errorMessageFn: (r) =>
        `Journey plan code does not follow prescribed format in row ${r.__rowNum__ + 1}${INVALID_CHAR_2}`
    });

    const invalidVisit = validateField({
        row,
        field: "Visit day",
        setFileError: setFileError,
        resolve: resolve,
        validateFn: (v) => /^[1-7]$/.test(v),
        errorHeader: "Invalid Visit day",
        errorMessageFn: (r) =>  `Visit Day in row ${r.__rowNum__ + 1} is invalid${INVALID_CHAR_2}`
    })

    const invalidSequence = validateField({
        row,
        field: "Sequence",
        setFileError: setFileError,
        resolve: resolve,
        validateFn: (v) => /^[1-9]\d*$/.test(v),
        errorHeader: "Invalid Sequence",
        errorMessageFn: (r) =>  `Sequence in row ${r.__rowNum__ + 1} is invalid${INVALID_CHAR_2}`
    })

    const isSpecialChars = validateSpecialChar({ row, setFileError, resolve, selectedCountryCode });
    
    const isEmpty = getEmptyValues({ row, setFileError, resolve });
    if (isEmpty || invalidWeek || isSpecialChars || invalidVisit || invalidJpCode || invalidSequence) return true;
  }

  return false;
};

export const getValidations = ({ filtered, setFileError, resolve, selectedCountryCode }) => {
  if (!filtered.length) {
    setFileError({ header: EXCEL_Template, message: EXCEL_EMPTY_ERR_MSG });
    resolve("invalid");
    return true;
  }
  return ValidateOtherFields({ filtered, setFileError, resolve, selectedCountryCode });
};

export const getFormattedPJPData = (data, selectedCountryCode) => {
  const normalizedData = data.replace(/\r\n|\r/g, '\n');
  const excelData = normalizedData.split('\n');

  let formattedData = excelData
    .map((row, index) => {
      const rowData = splitCSVRow(row);
      const isEmpty = rowData.every((cell) => cell === "");
      if(index !== 0 && !isEmpty) {
        return {
          "Customer code": rowData[0],
          "Journey plan code": rowData[1],
          "Journey plan description": rowData[2],
          "Sequence": rowData[3],
          "Week": rowData[4],
          "Visit day": rowData[5],
          __rowNum__: index,
        }
      }
      return null;
    })
    .filter(Boolean);

  return formattedData;
};


const getPJPHeader = (data, selectedCountryCode) => {
  const headerEnd = data.search(/[\r\n]/);
  const headerLine = data.slice(0, headerEnd === -1 ? data.length : headerEnd).replace(/^\uFEFF/, "");
  const header = splitCSVRow(headerLine).map(h => h.trim());
  if (selectedCountryCode === "id") {
    return true;
  } else {
    if (isSameArrays(header, ["Customer code", "Journey plan code", "Journey plan description", 
      "Sequence", "Week", "Visit day"])) {
      return header;
    }
  }
  return false;
}

export const ValidateExcel = (file, setFileError, selectedCountryCode) => {
  return new Promise((resolve, reject) => {
    if (!validateFileMeta(file, setFileError)) {
      resolve("invalid");
      return;
    }
    if (selectedCountryCode === "id") {
      resolve("valid");
      return; 
      }
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = reader.result;
      const pjpHeader = getPJPHeader(data, selectedCountryCode);
      if (!pjpHeader) {
        setFileError({ header: HEADER_ERR, message: HEADER_ERR_MESSAGE });
        resolve("invalid");
        return;
      }
      let formattedData = getFormattedPJPData(data, selectedCountryCode);
      const filtered = formattedData.filter((row) =>
        Object.values(row).some((v) => v !== "")
      );
      getValidations({ filtered, setFileError, resolve, selectedCountryCode });
      resolve("valid");
    };
    reader.onerror = (error) => {
      console.log(error);
      reject(error);
    };
    reader.readAsText(file);
  });
};
